/**
 * PlanilhaInstalacoesCard (ADR-038, spec visitas-sem-copia, design §9, Req 1) — o cartão "Planilha de instalações sem
 * termo" em Cargas, só para o Administrador (RN-M2-001):
 *  1. escolher o `.xlsx` (só esse formato); o SHA-256 é calculado no navegador;
 *  2. registrar e enviar pela URL assinada (o mesmo arquivo já no acervo não é reenviado);
 *  3. a prévia — só números, a aba lida, as colunas ausentes e os motivos das linhas marcadas, nunca valores de célula
 *     (RN-GLOBAL-004);
 *  4. "Confirmar carga" → o andamento do lote e, ao fim, o relatório com os alertas por motivo.
 * ADR-040 (spec carga-com-planilhas): importa tudo, como está — INEP inválido ou repetido vira visita marcada, coluna
 * ausente vira campo vazio marcado. Só o arquivo que não dá para estruturar é recusado (422 `FORMATO_PLANILHA`, com a
 * mensagem do motivo). Na carga de pasta, a planilha é importada sozinha — este cartão é a carga avulsa.
 */

import { ChangeEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import { useAuth, hasAnyProfile } from '../contexts/AuthContext';
import { ApiError } from '../services/apiClient';
import { sha256File } from '../services/fileHash';
import {
  AnalisePlanilha,
  FormatoPlanilhaDetalhes,
  PlanilhaDetalhe,
  PreviaPlanilha,
} from '../services/planilhas-types';

const numero = new Intl.NumberFormat('pt-BR');

/** Os números da prévia, na ordem em que aparecem (Req 1.3). */
const LINHAS_PREVIA: ReadonlyArray<readonly [Exclude<keyof PreviaPlanilha, 'aba' | 'colunasAusentes'>, string]> = [
  ['linhas', 'Linhas na planilha'],
  ['novas', 'Novas (viram visita)'],
  ['referencias', 'Referências (a escola já tem termo com imagem)'],
  ['jaCarregadas', 'Já carregadas (a escola veio de outra planilha)'],
  ['inepInvalido', 'INEP inválido (viram visita marcada)'],
  ['inepRepetido', 'INEP repetido (uma visita por linha, marcadas)'],
  ['emTrabalho', '"EM TRABALHO"'],
  ['iccidPerdido', 'ICCID perdido (gravado como número pelo Excel)'],
  ['coordenadaSemSeparador', 'Coordenada sem separador decimal'],
  ['semData', 'Sem data (1900-01-01)'],
];

interface ErroCarga {
  readonly mensagem: string;
  readonly detalhes?: FormatoPlanilhaDetalhes;
}

/** O 422 `FORMATO_PLANILHA` traz a mensagem segura e o que falta; o resto vira um aviso genérico. */
function erroDaApi(err: unknown): ErroCarga {
  if (err instanceof ApiError && err.status === 422) {
    const corpo = err.body as { error?: { code?: string; message?: string; details?: FormatoPlanilhaDetalhes } } | undefined;
    if (corpo?.error?.code === 'FORMATO_PLANILHA') {
      return { mensagem: corpo.error.message ?? 'A planilha não está no formato esperado.', detalhes: corpo.error.details };
    }
  }
  return { mensagem: 'Não foi possível carregar a planilha. Tente de novo.' };
}

export function PlanilhaInstalacoesCard({ intervaloMs = 5000 }: { readonly intervaloMs?: number }): JSX.Element | null {
  const ehAdmin = hasAnyProfile(useAuth(), ['admin']);
  const { planilhas } = useServices();
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState<ErroCarga | null>(null);
  const [analise, setAnalise] = useState<AnalisePlanilha | null>(null);
  const [jaCarregada, setJaCarregada] = useState(false);
  const [acompanhado, setAcompanhado] = useState<string | null>(null);

  // O andamento do lote até concluir; depois, o relatório.
  const { data: detalhe } = useQuery<PlanilhaDetalhe>({
    queryKey: ['planilha-instalacoes', acompanhado],
    queryFn: () => planilhas.obter(acompanhado as string),
    enabled: acompanhado !== null,
    refetchInterval: (q) => (q.state.data?.andamento?.concluido ? false : intervaloMs),
  });

  if (!ehAdmin) {
    return null;
  }

  const recomecar = (): void => {
    setErro(null);
    setAnalise(null);
    setJaCarregada(false);
    setAcompanhado(null);
  };

  async function escolher(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    recomecar();
    if (!arquivo.name.toLowerCase().endsWith('.xlsx')) {
      setErro({ mensagem: 'Escolha o arquivo .xlsx da planilha.' });
      return;
    }
    setProcessando(true);
    try {
      const sha256 = await sha256File(arquivo);
      const registro = await planilhas.registrar(arquivo.name, arquivo.size, sha256);
      if (registro.status === 'confirmada') {
        // A mesma planilha já foi carregada (idempotente): mostra o andamento e o relatório dela.
        setJaCarregada(true);
        setAcompanhado(registro.planilhaId);
        return;
      }
      if (registro.upload) {
        await planilhas.enviar(registro.upload, arquivo);
      }
      setAnalise(await planilhas.analisar(registro.planilhaId));
    } catch (err) {
      setErro(erroDaApi(err));
    } finally {
      setProcessando(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (!analise) return;
    setProcessando(true);
    setErro(null);
    try {
      await planilhas.confirmar(analise.planilhaId);
      setAcompanhado(analise.planilhaId);
    } catch (err) {
      setErro(erroDaApi(err));
    } finally {
      setProcessando(false);
    }
  }

  return (
    <section className="card">
      <h2>Planilha de instalações sem termo</h2>
      <p className="muted">
        As escolas instaladas sem o termo digitalizado entram como visitas "Planilha (sem imagem)", conferidas pelas
        mesmas regras. A escola que já tem termo com imagem fica como referência (o termo prevalece).
      </p>

      <div className="form-field">
        <label htmlFor="planilha-instalacoes">Planilha (.xlsx)</label>
        <input
          id="planilha-instalacoes"
          type="file"
          accept=".xlsx"
          disabled={processando}
          onChange={(e) => void escolher(e)}
        />
      </div>
      {processando && <p className="muted">Processando…</p>}

      {erro && (
        <div className="notice" role="alert">
          <p>{erro.mensagem}</p>
        </div>
      )}

      {analise && acompanhado === null && <Previa analise={analise} confirmando={processando} onConfirmar={() => void confirmar()} />}

      {jaCarregada && <p className="notice notice-info">Esta planilha já foi carregada.</p>}
      {acompanhado !== null && detalhe && <Andamento detalhe={detalhe} />}
    </section>
  );
}

function Previa({
  analise,
  confirmando,
  onConfirmar,
}: {
  readonly analise: AnalisePlanilha;
  readonly confirmando: boolean;
  readonly onConfirmar: () => void;
}): JSX.Element {
  return (
    <div data-testid="previa-planilha">
      <h3>Prévia</h3>
      <p className="muted">{`Aba lida: ${analise.previa.aba}`}</p>
      {analise.previa.colunasAusentes.length > 0 && (
        <p className="notice notice-info">{`Colunas ausentes (campo vazio, marcado): ${analise.previa.colunasAusentes.join(', ')}`}</p>
      )}
      <table className="table">
        <tbody>
          {LINHAS_PREVIA.map(([chave, rotulo]) => (
            <tr key={chave}>
              <th>{rotulo}</th>
              <td>{numero.format(analise.previa[chave])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {analise.linhasMarcadas.length > 0 && (
        <>
          <h3>Linhas marcadas (entram como visita, com alerta)</h3>
          <ul>
            {analise.linhasMarcadas.map((l) => (
              <li key={l.linha}>{`Linha ${l.linha}: ${l.motivo}`}</li>
            ))}
          </ul>
        </>
      )}
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={onConfirmar} disabled={confirmando || analise.previa.novas + analise.previa.referencias === 0}>
          Confirmar carga
        </button>
      </div>
    </div>
  );
}

function Andamento({ detalhe }: { readonly detalhe: PlanilhaDetalhe }): JSX.Element {
  const a = detalhe.andamento;
  const r = detalhe.relatorio;
  if (r && a?.concluido) {
    return (
      <div data-testid="relatorio-planilha">
        <h3>Relatório</h3>
        <p>{r.visitasGravadas === 1 ? '1 visita gravada' : `${numero.format(r.visitasGravadas)} visitas gravadas`}</p>
        <p className="muted">
          {`Referências: ${numero.format(r.referencias)} · já carregadas: ${numero.format(r.jaCarregadas)} · falhas: ${numero.format(r.falhas)}`}
        </p>
        <p>{`Com alerta: ${numero.format(r.comAlerta)}`}</p>
        <ul>
          <li>{`ICCID perdido: ${numero.format(r.alertasPorMotivo.iccid_perdido)}`}</li>
          <li>{`"EM TRABALHO": ${numero.format(r.alertasPorMotivo.em_trabalho)}`}</li>
          <li>{`Coordenada não recuperada: ${numero.format(r.alertasPorMotivo.coordenada_nao_recuperada)}`}</li>
          {r.alertasPorMotivo.coluna_ausente !== undefined && (
            <>
              <li>{`Coluna ausente na planilha: ${numero.format(r.alertasPorMotivo.coluna_ausente)}`}</li>
              <li>{`INEP repetido: ${numero.format(r.alertasPorMotivo.inep_repetido ?? 0)}`}</li>
              <li>{`INEP inválido não corrigido: ${numero.format(r.alertasPorMotivo.inep_invalido ?? 0)}`}</li>
            </>
          )}
        </ul>
      </div>
    );
  }
  return (
    <p className="muted" role="status">
      {a
        ? `Conferindo as linhas: ${numero.format(a.ok + a.falha)} de ${numero.format(a.total)}${a.falha > 0 ? ` (${numero.format(a.falha)} com falha)` : ''}.`
        : 'Carga confirmada — preparando a conferência das linhas…'}
    </p>
  );
}
